<template>
  <!-- Editor de Etiquetas WYSIWYG compartido (Maestro / Inventario) -->
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="1000px" scrollable>
    <v-card>
      <v-toolbar flat dense color="white">
        <v-icon left color="primary">mdi-label-multiple-outline</v-icon>
        <v-toolbar-title class="subtitle-1 font-weight-medium">Editor de Etiquetas</v-toolbar-title>
        <v-spacer />
        <v-chip small outlined color="primary" class="mr-2">
          {{ totalLabelCount }} etiqueta{{ totalLabelCount !== 1 ? 's' : '' }}
        </v-chip>
        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-divider />

      <v-card-text class="pa-0" style="max-height: 620px; overflow: auto;">
        <v-row no-gutters>

          <!-- Panel izquierdo: Campos -->
          <v-col cols="3" class="wysiwyg-config-panel pa-3">
            <div class="editor-section-label mb-2">PAPEL</div>
            <v-btn-toggle v-model="labelFormat" mandatory dense class="format-toggle mb-4">
              <v-btn value="cab" x-small class="format-btn">
                <v-icon x-small left>mdi-view-grid</v-icon>CAB 33×25
              </v-btn>
              <v-btn value="zebra" x-small class="format-btn">
                <v-icon x-small left>mdi-label-outline</v-icon>Zebra 50×30
              </v-btn>
            </v-btn-toggle>

            <div class="editor-section-label mb-1">CAMPOS</div>
            <div class="caption grey--text mb-2" style="font-size:0.62rem">
              ojo=mostrar · +/- = tamaño fuente
            </div>
            <div
              v-for="field in labelFields"
              :key="field.id"
              class="wysiwyg-field-row"
              :class="{ 'wysiwyg-field-selected': selectedFieldId === field.id }"
              @click="selectedFieldId = field.id"
            >
              <v-btn icon x-small class="mr-1" @click.stop="toggleFieldVisibility(field)">
                <v-icon x-small :color="field.visible ? 'primary' : 'grey lighten-1'">
                  {{ field.visible ? 'mdi-eye' : 'mdi-eye-off' }}
                </v-icon>
              </v-btn>

              <span class="text-caption flex-grow-1 label-field-name"
                :class="field.visible ? 'black--text' : 'grey--text'">
                {{ field.label }}
              </span>

              <v-btn icon x-small @click.stop="changeFontSize(field, -1)">
                <v-icon x-small>mdi-minus</v-icon>
              </v-btn>
              <span class="font-size-badge">{{ field.fontSize }}</span>
              <v-btn icon x-small @click.stop="changeFontSize(field, +1)">
                <v-icon x-small>mdi-plus</v-icon>
              </v-btn>

            </div>

            <v-btn x-small text color="grey" class="mt-3" block @click="resetLabelPositions">
              <v-icon x-small left>mdi-restore</v-icon>Restablecer
            </v-btn>
          </v-col>

          <v-divider vertical />

          <!-- Panel derecho: Canvas + Tabla -->
          <v-col cols="9" class="pa-4">
            <div class="editor-section-label mb-1">
              DISEÑO
              <span class="grey--text text-caption font-weight-regular ml-2">
                arrastra los campos · {{ labelFormat === 'cab' ? '33×25 mm · CAB · 3 col/hoja' : '50×30 mm · Zebra · individual' }}
              </span>
            </div>
            <div class="canvas-area mb-4">
              <div
                class="label-canvas"
                :style="{ width: labelCanvasW + 'px', height: labelCanvasH + 'px' }"
                @mousemove.prevent="onCanvasMouseMove"
                @mouseup="onCanvasMouseUp"
                @mouseleave="onCanvasMouseUp"
              >
                <template v-for="field in labelFields">
                <div
                  v-if="field.visible"
                  :key="field.id"
                  class="canvas-field"
                  :class="{
                    'canvas-field-selected': selectedFieldId === field.id,
                    'canvas-field-dragging': draggingFieldId === field.id,
                  }"
                  :style="getFieldCanvasStyle(field)"
                  @mousedown.prevent="startFieldDrag($event, field)"
                  @click.stop="selectedFieldId = field.id"
                >
                  <template v-if="field.id === 'ean'">
                    <div class="canvas-barcode"
                      :style="{ height: Math.round(labelScale * (labelFormat === 'cab' ? 3.5 : 5)) + 'px', width: Math.round(labelCanvasW * 0.68) + 'px' }">
                    </div>
                    <div :style="{ fontSize: getFieldCanvasFontSize(field) + 'px', fontFamily: 'monospace', marginTop: '1px', lineHeight: 1 }">
                      {{ getPreviewItem().ean || '7706000000000' }}
                    </div>
                  </template>
                  <template v-else>
                    <span :style="{
                      fontSize: getFieldCanvasFontSize(field) + 'px',
                      fontWeight: field.id === 'precio' ? '900' : 'normal',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.1,
                    }">{{ getFieldDisplayValue(field) }}</span>
                  </template>
                </div>
                </template>
              </div>
            </div>

            <v-divider class="mb-3" />

            <div class="editor-section-label mb-2">CANTIDAD POR TALLA
              <span class="grey--text text-caption font-weight-regular ml-2">click = previsualizar</span>
            </div>
            <v-simple-table dense class="label-items-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">EAN</th>
                    <th class="text-left">Talla · Color</th>
                    <th class="text-right" style="width:130px">Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, i) in labelItems"
                    :key="item.ean"
                    class="qty-row"
                    :class="{ 'active-preview-row': i === previewIndex }"
                    @click="previewIndex = i"
                  >
                    <td class="text-caption grey--text">{{ item.ean }}</td>
                    <td class="text-caption">
                      <v-chip x-small label color="primary" outlined class="mr-1">
                        {{ item.talla || '—' }}
                      </v-chip>
                      <span v-if="item.color" class="grey--text">{{ item.color }}</span>
                    </td>
                    <td>
                      <div class="d-flex align-center justify-end">
                        <v-btn icon x-small @click.stop="adjustQty(i, -1)" :disabled="item.cantidadImprimir <= 0">
                          <v-icon x-small>mdi-minus</v-icon>
                        </v-btn>
                        <v-text-field
                          :value="item.cantidadImprimir"
                          type="number"
                          min="0"
                          dense
                          hide-details
                          outlined
                          class="qty-input mx-2"
                          @click.stop
                          @input="setQty(i, $event)"
                        ></v-text-field>
                        <v-btn icon x-small @click.stop="adjustQty(i, 1)">
                          <v-icon x-small>mdi-plus</v-icon>
                        </v-btn>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-col>

        </v-row>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4">
        <v-btn text @click="close">Cancelar</v-btn>
        <v-spacer />
        <v-btn color="primary" :disabled="totalLabelCount === 0" @click="downloadMasterZPL">
          <v-icon left>mdi-download</v-icon>
          Descargar ZPL · {{ totalLabelCount }} etiqueta{{ totalLabelCount !== 1 ? 's' : '' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "LabelEditor",
  props: {
    // v-model: controla la visibilidad del diálogo
    value: {
      type: Boolean,
      default: false,
    },
    // Productos crudos seleccionados a etiquetar
    items: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      labelFormat: "cab",
      labelItems: [],
      previewIndex: 0,
      selectedFieldId: null,
      draggingFieldId: null,
      dragStart: { mx: 0, my: 0, fx: 0, fy: 0 },
      labelFields: [
        { id: "nombre",     label: "Nombre",      icon: "mdi-package-variant",  visible: true,  fontSize: 12, x: 1,  y: 1  },
        { id: "marca",      label: "Marca",        icon: "mdi-star-outline",     visible: false, fontSize: 8,  x: 20, y: 1  },
        { id: "referencia", label: "Referencia",  icon: "mdi-tag",              visible: true,  fontSize: 8,  x: 1,  y: 8  },
        { id: "talla",      label: "Talla",       icon: "mdi-ruler",            visible: true,  fontSize: 8,  x: 22, y: 8  },
        { id: "color",      label: "Color",       icon: "mdi-palette",          visible: false, fontSize: 8,  x: 1,  y: 13 },
        { id: "ean",        label: "EAN/Barcode", icon: "mdi-barcode",          visible: true,  fontSize: 7,  x: 1,  y: 13 },
        { id: "precio",     label: "Precio",      icon: "mdi-currency-usd",     visible: true,  fontSize: 16, x: 1,  y: 19 },
        { id: "costo",      label: "Costo (cod)", icon: "mdi-lock-outline",     visible: false, fontSize: 7,  x: 22, y: 19 },
        { id: "sku",        label: "SKU",         icon: "mdi-identifier",       visible: false, fontSize: 7,  x: 1,  y: 23 },
        { id: "fecha",      label: "Fecha/Lote",  icon: "mdi-calendar-outline", visible: false, fontSize: 7,  x: 20, y: 23 },
        { id: "tienda",     label: "Tienda",      icon: "mdi-storefront-outline", visible: false, fontSize: 7, x: 1, y: 5 },
        { id: "proveedor",  label: "Proveedor",   icon: "mdi-truck-outline",    visible: false, fontSize: 7,  x: 20, y: 5 },
      ],
    };
  },
  computed: {
    totalLabelCount() {
      return this.labelItems.reduce((sum, i) => sum + (i.cantidadImprimir || 0), 0);
    },

    // px per mm for the canvas display
    labelScale() {
      return this.labelFormat === "cab" ? 7 : 6;
    },

    // canvas size in pixels
    labelCanvasW() {
      return this.labelFormat === "cab" ? Math.round(33 * this.labelScale) : Math.round(50 * this.labelScale);
    },

    labelCanvasH() {
      return this.labelFormat === "cab" ? Math.round(25 * this.labelScale) : Math.round(30 * this.labelScale);
    },
  },
  mounted() {
    this.loadLabelConfig();
  },
  watch: {
    // Al abrirse el diálogo, preparar los items a etiquetar desde la prop.
    value(open) {
      if (open) this.prepareItems();
    },
    labelFields: {
      handler() { this.saveLabelConfig(); },
      deep: true,
    },
    labelFormat() { this.saveLabelConfig(); },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },

    // Normaliza los productos recibidos y reinicia el estado de previsualización.
    // El color en Inventario llega como `colorName`; en Maestro como `color`.
    prepareItems() {
      this.labelItems = (this.items || []).map((it) => ({
        ...it,
        color: it.color || it.colorName || "",
        cantidadImprimir: 1,
      }));
      this.previewIndex = 0;
      this.selectedFieldId = null;
    },

    loadLabelConfig() {
      try {
        const saved = localStorage.getItem("master_label_config");
        if (!saved) return;
        const data = JSON.parse(saved);
        if (data.labelFormat) this.labelFormat = data.labelFormat;
        if (Array.isArray(data.labelFields)) {
          data.labelFields.forEach((sf) => {
            const field = this.labelFields.find((f) => f.id === sf.id);
            if (!field) return;
            field.visible = sf.visible;
            field.fontSize = sf.fontSize;
            field.x = sf.x;
            field.y = sf.y;
          });
        }
      } catch (_) { /* ignorar localStorage corrupto */ }
    },

    saveLabelConfig() {
      const data = {
        labelFormat: this.labelFormat,
        labelFields: this.labelFields.map((f) => ({
          id: f.id, visible: f.visible, fontSize: f.fontSize, x: f.x, y: f.y,
        })),
      };
      localStorage.setItem("master_label_config", JSON.stringify(data));
    },

    // Devuelve el item que se muestra en el canvas
    getPreviewItem() {
      return this.labelItems[this.previewIndex] || this.labelItems[0] || {
        nombre: "Camiseta Ejemplo", refe: "CAM001", talla: "M", color: "Azul",
        ean: "7706123456789", precio: 59900, descuento: 0, marca: "Marca", sku: "CAM001-M-AZU",
        id_proveedor: "PROV01",
      };
    },

    // Retorna el estilo CSS para un campo en el canvas
    getFieldCanvasStyle(field) {
      return {
        position: "absolute",
        left: Math.round(field.x * this.labelScale) + "px",
        top: Math.round(field.y * this.labelScale) + "px",
        cursor: this.draggingFieldId === field.id ? "grabbing" : "grab",
        padding: "1px 2px",
        borderRadius: "2px",
        zIndex: this.selectedFieldId === field.id ? 10 : 1,
        userSelect: "none",
      };
    },

    // Convierte fontSize (pt) a píxeles de canvas
    getFieldCanvasFontSize(field) {
      // 1pt = 0.353mm; at labelScale px/mm → px = pt * 0.353 * scale
      return Math.max(6, Math.round(field.fontSize * 0.353 * this.labelScale));
    },

    // Nemotecnia del costo: AD<costo>XC, con los ceros reemplazados por "C"
    // para hacerlo aún más opaco al cliente. Ej: 59900 -> AD599CCXC
    formatCostMnemonic(costo) {
      const digits = Math.round(parseFloat(costo || 0)).toString().replace(/0/g, "C");
      return `AD${digits}XC`;
    },

    // Retorna el texto a mostrar en el canvas para un campo
    getFieldDisplayValue(field) {
      const item = this.getPreviewItem();
      const price = parseFloat(item.precio || 0) * (1 - parseFloat(item.descuento || 0) / 100);
      const today = new Date();
      const dateStr = `${today.getDate().toString().padStart(2, "0")}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getFullYear().toString().slice(-2)}`;
      switch (field.id) {
        case "nombre":     return (item.nombre || item.refe || "Nombre").substring(0, 22);
        case "referencia": return `Ref: ${item.refe || "REF001"}`;
        case "talla":      return `T: ${item.talla || "M"}`;
        case "color":      return item.color || "Color";
        case "precio":     return `$ ${price.toLocaleString("es-CO", { minimumFractionDigits: 0 })}`;
        case "marca":      return item.marca || "Marca";
        case "costo":      return this.formatCostMnemonic(item.costo);
        case "sku":        return `SKU: ${item.sku || "SKU-001"}`;
        case "fecha":      return dateStr;
        case "tienda":     return (window.store && window.store.store_name) || "Tienda";
        case "proveedor":  return `Prov: ${item.id_proveedor || "—"}`;
        default:           return field.label;
      }
    },

    // Muestra/oculta un campo
    toggleFieldVisibility(field) {
      this.$set(field, "visible", !field.visible);
    },

    // Cambia el tamaño de fuente de un campo (reactivo)
    changeFontSize(field, delta) {
      const newSize = Math.max(5, Math.min(60, (field.fontSize || 10) + delta));
      this.$set(field, "fontSize", newSize);
    },

    // Inicia el arrastre de un campo en el canvas
    startFieldDrag(event, field) {
      this.draggingFieldId = field.id;
      this.selectedFieldId = field.id;
      this.dragStart = {
        mx: event.clientX,
        my: event.clientY,
        fx: field.x,
        fy: field.y,
      };
    },

    // Actualiza posición mientras arrastra
    onCanvasMouseMove(event) {
      if (!this.draggingFieldId) return;
      const dx = (event.clientX - this.dragStart.mx) / this.labelScale;
      const dy = (event.clientY - this.dragStart.my) / this.labelScale;
      const field = this.labelFields.find((f) => f.id === this.draggingFieldId);
      if (!field) return;
      const maxW = this.labelFormat === "cab" ? 33 : 50;
      const maxH = this.labelFormat === "cab" ? 25 : 30;
      this.$set(field, "x", Math.max(0, Math.min(maxW - 2, parseFloat((this.dragStart.fx + dx).toFixed(1)))));
      this.$set(field, "y", Math.max(0, Math.min(maxH - 2, parseFloat((this.dragStart.fy + dy).toFixed(1)))));
    },

    // Finaliza el arrastre
    onCanvasMouseUp() {
      this.draggingFieldId = null;
    },

    // Restablece las posiciones por defecto
    resetLabelPositions() {
      const defaults = {
        nombre:     { x: 1,  y: 1  }, marca:  { x: 20, y: 1  },
        referencia: { x: 1,  y: 8  }, talla:  { x: 22, y: 8  },
        color:      { x: 1,  y: 13 }, ean:    { x: 1,  y: 13 },
        precio:     { x: 1,  y: 19 }, costo:  { x: 22, y: 19 },
        sku:        { x: 1,  y: 23 }, fecha:  { x: 20, y: 23 },
        tienda:     { x: 1,  y: 5  }, proveedor: { x: 20, y: 5 },
      };
      this.labelFields.forEach((f) => {
        if (defaults[f.id]) {
          this.$set(f, "x", defaults[f.id].x);
          this.$set(f, "y", defaults[f.id].y);
        }
      });
    },

    adjustQty(i, delta) {
      const newVal = (this.labelItems[i].cantidadImprimir || 0) + delta;
      this.$set(this.labelItems[i], "cantidadImprimir", Math.max(0, newVal));
    },

    setQty(i, value) {
      const n = Math.max(0, Math.floor(parseFloat(value) || 0));
      this.$set(this.labelItems[i], "cantidadImprimir", n);
    },

    downloadMasterZPL() {
      let zpl = "";
      if (this.labelFormat === "cab") {
        zpl = this._generateCABZpl();
      } else {
        this.labelItems.forEach((item) => {
          for (let i = 0; i < (item.cantidadImprimir || 0); i++) {
            zpl += this._generateZebraLabel(item);
          }
        });
      }
      const blob = new Blob([zpl], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `etiquetas_${new Date().toISOString().slice(0, 10)}.zpl`;
      a.click();
      window.URL.revokeObjectURL(url);
      this.close();
      this.$emit("show-notification", `ZPL generado: ${this.totalLabelCount} etiquetas`, "success");
    },

    // CAB: 3 etiquetas por fila. Cada etiqueta es 33×25mm = 264×200 dots @203dpi
    _generateCABZpl() {
      const allLabels = [];
      this.labelItems.forEach((item) => {
        for (let i = 0; i < (item.cantidadImprimir || 0); i++) allLabels.push(item);
      });
      // xOffsets en dots: etiqueta 1 empieza en ~45, 2 en ~309, 3 en ~573 (ajustar según impresora)
      const colDots = Math.round(33 * 8); // 264 dots por columna
      const xOffsets = [5, 5 + colDots, 5 + colDots * 2];
      let zpl = "";
      for (let i = 0; i < allLabels.length; i += 3) {
        zpl += "^XA\n";
        for (let j = 0; j < 3 && i + j < allLabels.length; j++) {
          zpl += this._buildCABColumn(allLabels[i + j], xOffsets[j]);
        }
        zpl += "^XZ\n";
      }
      return zpl;
    },

    // Genera los comandos ZPL para una columna de la etiqueta CAB
    _buildCABColumn(item, xOffset) {
      const price = parseFloat(item.precio || 0) * (1 - parseFloat(item.descuento || 0) / 100);
      const today = new Date();
      const dateStr = `${today.getDate().toString().padStart(2, "0")}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getFullYear().toString().slice(-2)}`;
      let zpl = "";

      for (const field of this.labelFields) {
        if (!field.visible) continue;
        // Posición en dots: mm * 8 + offset de columna para X; mm * 8 para Y
        const xd = Math.round(field.x * 8) + xOffset;
        const yd = Math.round(field.y * 8);
        const fh = Math.max(8, Math.round(field.fontSize * 2.82)); // pt → dots

        switch (field.id) {
          case "nombre":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${(item.nombre || item.refe || "").substring(0, 18)}^FS\n`;
            break;
          case "referencia":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDRef:${(item.refe || "").substring(0, 10)}^FS\n`;
            break;
          case "talla":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDT:${item.talla || ""}^FS\n`;
            break;
          case "color":
            if (item.color) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${item.color}^FS\n`;
            break;
          case "marca":
            if (item.marca) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${item.marca}^FS\n`;
            break;
          case "sku":
            if (item.sku) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDSKU:${item.sku}^FS\n`;
            break;
          case "precio":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${price.toLocaleString("es-CO", { minimumFractionDigits: 0 })}^FS\n`;
            break;
          case "costo":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${this.formatCostMnemonic(item.costo)}^FS\n`;
            break;
          case "ean":
            if (item.ean) {
              const bh = Math.round(3.5 * 8); // 3.5mm en dots
              zpl += `^FO${xd},${yd}^BY1,2,${bh}^BCN,${bh},N,N,N^FD${item.ean}^FS\n`;
              zpl += `^FO${xd},${yd + bh + 2}^A0N,10,10^FD${item.ean}^FS\n`;
            }
            break;
          case "fecha":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${dateStr}^FS\n`;
            break;
          case "tienda": {
            const storeName = ((window.store && window.store.store_name) || "").substring(0, 18);
            if (storeName) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${storeName}^FS\n`;
            break;
          }
          case "proveedor":
            if (item.id_proveedor) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDProv:${item.id_proveedor}^FS\n`;
            break;
        }
      }
      return zpl;
    },

    // Genera etiqueta Zebra 50×30mm (400×240 dots @203dpi)
    _generateZebraLabel(item) {
      const price = parseFloat(item.precio || 0) * (1 - parseFloat(item.descuento || 0) / 100);
      const today = new Date();
      const dateStr = `${today.getDate().toString().padStart(2, "0")}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getFullYear().toString().slice(-2)}`;
      let zpl = "^XA\n^PW400\n^LL240\n";

      for (const field of this.labelFields) {
        if (!field.visible) continue;
        const xd = Math.round(field.x * 8);
        const yd = Math.round(field.y * 8);
        const fh = Math.max(8, Math.round(field.fontSize * 2.82));

        switch (field.id) {
          case "nombre":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${(item.nombre || item.refe || "").substring(0, 26)}^FS\n`;
            break;
          case "referencia":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDRef: ${item.refe || ""}^FS\n`;
            break;
          case "talla":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDT: ${item.talla || ""}^FS\n`;
            break;
          case "color":
            if (item.color) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${item.color}^FS\n`;
            break;
          case "marca":
            if (item.marca) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${item.marca}^FS\n`;
            break;
          case "sku":
            if (item.sku) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDSKU: ${item.sku}^FS\n`;
            break;
          case "precio":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD$ ${price.toLocaleString("es-CO", { minimumFractionDigits: 0 })}^FS\n`;
            break;
          case "costo":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${this.formatCostMnemonic(item.costo)}^FS\n`;
            break;
          case "ean":
            if (item.ean) {
              const bh = Math.round(5 * 8); // 5mm en dots
              zpl += `^FO${xd},${yd}^BY2,2,${bh}^BCN,${bh},Y,N,N^FD${item.ean}^FS\n`;
            }
            break;
          case "fecha":
            zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${dateStr}^FS\n`;
            break;
          case "tienda": {
            const storeName = ((window.store && window.store.store_name) || "").substring(0, 26);
            if (storeName) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FD${storeName}^FS\n`;
            break;
          }
          case "proveedor":
            if (item.id_proveedor) zpl += `^FO${xd},${yd}^A0N,${fh},${fh}^FDProv: ${item.id_proveedor}^FS\n`;
            break;
        }
      }
      zpl += "^XZ\n";
      return zpl;
    },
  },
};
</script>

<style scoped>
/* ── Label Editor WYSIWYG ─────────────────────── */
.editor-section-label {
  font-size: 0.68rem;
  font-weight: 700;
  color: #999;
  letter-spacing: 0.08em;
}
.format-toggle {
  width: 100%;
}
.format-btn {
  flex: 1 !important;
  font-size: 0.75rem !important;
}

/* Config panel */
.wysiwyg-config-panel {
  background: #fafafa;
  border-right: none;
  min-width: 210px;
}
.wysiwyg-field-row {
  display: flex;
  align-items: center;
  padding: 4px 2px;
  border-radius: 5px;
  cursor: pointer;
  margin-bottom: 1px;
  transition: background 0.12s;
  user-select: none;
}
.wysiwyg-field-row:hover {
  background: #f0f4ff;
}
.wysiwyg-field-selected {
  background: #e3edff;
}
.label-field-name {
  font-size: 0.72rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90px;
}
.font-size-badge {
  font-size: 0.7rem;
  font-weight: 700;
  color: #555;
  min-width: 18px;
  text-align: center;
}

/* WYSIWYG canvas */
.canvas-area {
  display: flex;
  align-items: flex-start;
}
.label-canvas {
  position: relative;
  background: #ffffff;
  border: 2px dashed #bbb;
  border-radius: 3px;
  box-shadow: 2px 3px 12px rgba(0,0,0,0.12);
  overflow: hidden;
  cursor: default;
  flex-shrink: 0;
}
.canvas-field {
  position: absolute;
  display: inline-block;
  line-height: 1.1;
}
.canvas-field-selected {
  outline: 1px dashed #1976d2;
  background: rgba(25, 118, 210, 0.05);
  border-radius: 2px;
}
.canvas-field-dragging {
  opacity: 0.75;
}
.canvas-barcode {
  background: repeating-linear-gradient(
    90deg,
    #000 0, #000 1.5px,
    #fff 1.5px, #fff 3px
  );
  width: 100%;
}

/* Quantity table */
.label-items-table th {
  font-size: 0.72rem !important;
  color: #666 !important;
  font-weight: 600 !important;
  padding: 6px 10px !important;
  background: #fafafa !important;
}
.label-items-table td {
  padding: 6px 10px !important;
  border-bottom: 1px solid #f0f0f0;
}
.qty-row {
  cursor: pointer;
  transition: background 0.15s;
}
.qty-row:hover {
  background: #f5f5f5;
}
.active-preview-row {
  background: #e8f0fe !important;
}
.qty-input {
  max-width: 72px;
}
.qty-input >>> input {
  text-align: center;
  font-weight: 700;
  padding: 4px 0;
}
</style>
